'use client';

import { useState } from 'react';
import Link from 'next/link';
import PccVerify, { type PccResult } from '../PccVerify';
import { useDuplicateCheck, DuplicateCheckButton } from '../DuplicateCheck';

const REF_ID_RE = /^[a-zA-Z0-9]{4,20}$/;

export default function SellerSignupClient({ pccEnabled }: { pccEnabled: boolean }) {
  const [ref, setRef] = useState('');
  const [name, setName] = useState('');
  const [nickname, setNickname] = useState('');
  const [phone, setPhone] = useState('');
  const [pccResult, setPccResult] = useState<PccResult | null>(null);
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const idCheck = useDuplicateCheck('id');
  const nicknameCheck = useDuplicateCheck('nickname');

  const handlePccVerified = (data: PccResult) => {
    setPccResult(data);
    if (data.name) setName(data.name);
    if (data.phone) setPhone(data.phone);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr('');
    if (!REF_ID_RE.test(ref)) { setErr('ID는 영문/숫자 4~20자로 입력해 주세요.'); return; }
    if (!idCheck.isValidFor(ref)) { setErr('ID 중복확인을 완료해 주세요.'); return; }
    if (password.length < 4) { setErr('비밀번호는 4자 이상이어야 합니다.'); return; }
    if (password !== passwordConfirm) { setErr('비밀번호가 일치하지 않습니다.'); return; }
    if (!nickname.trim()) { setErr('닉네임을 입력해 주세요.'); return; }
    if (!nicknameCheck.isValidFor(nickname)) { setErr('닉네임 중복확인을 완료해 주세요.'); return; }
    if (!pccResult) { setErr(pccEnabled ? '실명인증을 먼저 완료해 주세요.' : '현재 회원가입이 일시 중단되었습니다. 잠시 후 다시 시도해 주세요.'); return; }
    setSubmitting(true);
    const res = await fetch('/api/seller-signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ref, name, nickname, phone, password,
        verifiedName: pccResult?.name, verifiedPhone: pccResult?.phone, verifiedBirthdate: pccResult?.birthdate,
      }),
    });
    const data = await res.json();
    setSubmitting(false);
    if (res.ok) setDone(true);
    else setErr(data.error ?? '가입에 실패했습니다.');
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-start justify-center p-5 pb-28">
        <div className="bg-white rounded-xl p-8 shadow-md w-full max-w-sm text-center">
          <h1 className="text-lg font-bold text-black mb-2">가입 신청 완료</h1>
          <p className="text-sm text-gray-500 mb-6">관리자 승인 후 로그인하실 수 있습니다.</p>
          <Link href="/" className="text-blue-600 text-sm">← 메인으로</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex items-start justify-center p-5 pb-28">
      <div className="bg-white rounded-xl p-8 shadow-md w-full max-w-sm">
        <h1 className="text-xl font-bold text-black mb-6">영업회원 가입</h1>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="text-xs text-gray-500 mb-1 block">ID (영업자 링크 주소)</label>
            <input required value={ref} onChange={e => setRef(e.target.value.replace(/[^A-Za-z0-9]/g, ''))}
              placeholder="예: kim01"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900" />
            <DuplicateCheckButton value={ref} state={idCheck.state} onCheck={() => idCheck.check(ref)} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">닉네임 (채팅에 표시)</label>
            <input required value={nickname} onChange={e => setNickname(e.target.value)}
              placeholder="채팅에 표시될 닉네임"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900" />
            <DuplicateCheckButton value={nickname} state={nicknameCheck.state} onCheck={() => nicknameCheck.check(nickname)} />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">이름</label>
            <input readOnly required value={name}
              placeholder="실명인증 완료 시 자동으로 입력됩니다"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-900" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">휴대폰번호</label>
            <input readOnly required value={phone}
              placeholder="실명인증 완료 시 자동으로 입력됩니다"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-900 mb-2" />
            {pccEnabled ? (
              <PccVerify verified={!!pccResult} verifiedName={pccResult?.name ?? null} onVerified={handlePccVerified} />
            ) : (
              <p className="text-xs text-red-500">현재 회원가입이 일시 중단되었습니다. 잠시 후 다시 시도해 주세요.</p>
            )}
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">비밀번호</label>
            <input required type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="4자 이상"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">비밀번호 확인</label>
            <input required type="password" value={passwordConfirm} onChange={e => setPasswordConfirm(e.target.value)}
              placeholder="비밀번호를 한 번 더 입력해 주세요"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900" />
            {passwordConfirm && password !== passwordConfirm && (
              <p className="text-red-500 text-xs mt-1">비밀번호가 일치하지 않습니다.</p>
            )}
          </div>
          {err && <p className="text-red-500 text-xs">{err}</p>}
          <button type="submit" disabled={submitting}
            className="w-full bg-gray-800 text-white py-3 rounded-lg font-semibold hover:bg-gray-900 disabled:opacity-50">
            {submitting ? '신청 중...' : '가입 신청'}
          </button>
        </form>
        <div className="text-center mt-4 space-x-3">
          <Link href="/login" className="text-xs text-blue-600 hover:underline">이미 회원이신가요? 로그인</Link>
          <Link href="/" className="text-xs text-gray-400 hover:text-gray-600">← 메인으로</Link>
        </div>
      </div>
    </div>
  );
}
